import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import CatalogProductItemForm from './CatalogProductItemForm';
import CatalogKitItemForm from './CatalogKitItemForm';
import { createEmptyKit, newClientId } from './catalogCompositionUtils';

const emptyProductDraft = (entryType, displayOrder) => ({
  id: '',
  item_type: 'PRODUCT',
  product_id: '',
  display_name: '',
  display_description: '',
  badge_label: '',
  medias: [],
  display_order: displayOrder ?? 0,
  is_available: true,
  sku_selection_mode: 'ALL_ACTIVE',
  skus: [],
  resolved_skus: [],
  _productType: entryType === 'SERVICE' ? 'SERVICE' : 'PRODUCT',
});

const CatalogItemEditDialog = ({
  open,
  sessionId,
  mode,
  entryType,
  initialProductItem,
  initialKit,
  productItems,
  displayOrder,
  disabled,
  onClose,
  onSave,
  onDelete,
}) => {
  const { translate } = useTranslation();
  const [draftProduct, setDraftProduct] = useState(null);
  const [draftKit, setDraftKit] = useState(null);
  const hydratedSessionRef = useRef(null);

  useEffect(() => {
    if (!open) {
      hydratedSessionRef.current = null;
      setDraftProduct(null);
      setDraftKit(null);
      return;
    }
    if (!sessionId || hydratedSessionRef.current === sessionId) return;
    hydratedSessionRef.current = sessionId;

    if (entryType === 'KIT') {
      setDraftKit(initialKit ? { ...initialKit } : createEmptyKit(displayOrder ?? 0));
      setDraftProduct(null);
      return;
    }
    setDraftProduct(
      initialProductItem ? { ...initialProductItem } : emptyProductDraft(entryType, displayOrder),
    );
    setDraftKit(null);
  }, [open, sessionId, entryType, displayOrder]);

  const titleKey =
    entryType === 'KIT'
      ? mode === 'create'
        ? 'catalog.dialog_add_kit'
        : 'catalog.dialog_edit_kit'
      : entryType === 'SERVICE'
        ? mode === 'create'
          ? 'catalog.dialog_add_service'
          : 'catalog.dialog_edit_service'
        : mode === 'create'
          ? 'catalog.dialog_add_product'
          : 'catalog.dialog_edit_product';

  const handleSave = () => {
    if (entryType === 'KIT') {
      if (!draftKit?.display_name?.trim()) return;
      onSave({ entryType: 'KIT', kit: draftKit });
      return;
    }
    if (!draftProduct?.product_id) return;
    onSave({
      entryType,
      productItem: {
        ...draftProduct,
        id: draftProduct.id || newClientId(),
      },
    });
  };

  const canSave =
    entryType === 'KIT'
      ? Boolean(draftKit?.display_name?.trim())
      : Boolean(draftProduct?.product_id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{translate(titleKey)}</DialogTitle>
      <DialogContent dividers>
        {entryType === 'KIT' ? (
          <CatalogKitItemForm
            kit={draftKit}
            productItems={productItems}
            disabled={disabled}
            onChange={setDraftKit}
          />
        ) : (
          <CatalogProductItemForm
            item={draftProduct}
            disabled={disabled}
            productTypeFilter={entryType === 'SERVICE' ? 'SERVICE' : 'PRODUCT'}
            onChange={setDraftProduct}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Box>
          {mode === 'edit' && onDelete && (
            <Button color="error" disabled={disabled} onClick={onDelete}>
              {translate('catalog.remove_from_catalog')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>{translate('common.cancel')}</Button>
          <Button variant="contained" disabled={disabled || !canSave} onClick={handleSave}>
            {translate('common.save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogItemEditDialog;
